import React, { useRef } from 'react';
import Modal from '@/components/common/modal/Modal';
import Input from '@/components/common/input/Input';
import Button from '@/components/common/button/Button';
import { PlusCircle, Trash2, UploadCloud, XCircle } from 'lucide-react';
import styles from './QuestionEditorModal.module.css';
import { useQuestionEditor } from './useQuestionEditor';
import type { question } from '../../types.api';

interface QuestionEditorModalProps {
    isOpen: boolean;
    onClose: () => void;
    question: question | null;
    onSave: (updatedQuestion: any) => void;
}

const QuestionEditorModal: React.FC<QuestionEditorModalProps> = ({ isOpen, onClose, question, onSave }) => {
    const {
        formData,
        handleTextChange,
        handleOptionChange,
        handleAddOption,
        handleRemoveOption,
        handleFileSelected,
        handleRemoveImage,
    } = useQuestionEditor(question);

    const fileInputRef = useRef<HTMLInputElement>(null);

    if (!isOpen || !formData) return null;

    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <div className={styles.modalHeader}>
                <h2>Editar pregunta #{formData.content.session_question_number}</h2>
            </div>
            <div className={styles.modalBody}>
                <div className={styles.formGroup}>
                    <label htmlFor="questionText">Enunciado (soporta HTML)</label>
                    <textarea
                        id="questionText"
                        value={formData.content.text || ''}
                        onChange={handleTextChange}
                        className={styles.textarea}
                        rows={6}
                    />
                </div>

                <h3 className={styles.optionsTitle}>Opciones de respuesta</h3>
                {(formData.content.options ||[]).map((option: any, index: number) => (
                    <div key={index} className={styles.inputWithButton}>
                        <Input
                            id={`option-${index}`}
                            label={`Opción ${String.fromCharCode(65 + index)}`}
                            value={option.text}
                            onChange={(e) => handleOptionChange(index, e.target.value)}
                        />
                        <button
                            onClick={() => handleRemoveOption(index)}
                            className={styles.removeButton}
                            title="Eliminar opción"
                        >
                            <Trash2 size={18} />
                        </button>
                    </div>
                ))}
                <Button
                    variant="secondary"
                    onClick={handleAddOption}
                    className={styles.addButton}
                >
                    <PlusCircle size={16} /> Añadir opción
                </Button>

                <div className={styles.imageSection}>
                    <h4 className={styles.imageTitle}>Imagen de la pregunta</h4>
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={(e) => handleFileSelected(e.target.files?.[0] || null)}
                        style={{ display: 'none' }}
                        accept="image/*"
                    />
                    {formData.content.image_url ? (
                        <div className={styles.imagePreviewWrapper}>
                            <img
                                src={formData.content.image_url}
                                alt="Vista previa"
                                className={styles.imagePreview}
                            />
                            <Button
                                variant="secondary"
                                onClick={() => {
                                    if (window.confirm("¿Estás seguro de que quieres eliminar esta imagen?")) {
                                        handleRemoveImage();
                                    }
                                }}
                                className={styles.imageActionButton}
                            >
                                <XCircle size={16} /> Quitar imagen
                            </Button>
                        </div>
                    ) : (
                        <Button
                            variant="secondary"
                            onClick={() => fileInputRef.current?.click()}
                            className={styles.imageActionButton}
                        >
                            <UploadCloud size={16} /> Subir imagen
                        </Button>
                    )}
                </div>
            </div>

            <div className={styles.modalFooter}>
                <Button
                    variant="secondary"
                    onClick={onClose}
                >
                    Cancelar
                </Button>
                <Button
                    variant="primary"
                    onClick={() => onSave(formData)}
                >
                    Guardar cambios
                </Button>
            </div>
        </Modal>
    );
};

export default QuestionEditorModal;